import type { BuilderPricingVisibilitySettings } from "api-types/builders/builderTypes";
import type { ClientLocationWithLevel } from "api-types/locations/locationBaseTypes";
import type { ClientUser } from "api-types/users/usersResponses";
import { assertIsNotNullOrUndefined, ensureIsString } from "common/utils/assertOfType";
import { validateZipCode } from "common/utils/formValidators";
import { objectTypedKeys } from "common/utils/objectTypedKeys";
import Toggle from "lib/components/actions/Toggle";
import { TEMPLATE_CODE_DESCRIPTION } from "lib/components/builders/constants";
import { EXTERNAL_ID_OVERVIEW_MESSAGE } from "lib/constants/messages";
import { pricingVisibilitySettings } from "lib/constants/pricingVisibility";
import { useUserContext } from "lib/state/UserContextProvider";
import getUserFullName from "lib/utils/userName";
import type { ReactNode } from "react";
import { useRef, useState } from "react";
import cx from "ui/cx";
import Checkbox from "ui/forms/CheckboxOld";
import CountrySelector from "ui/forms/CountrySelector/CountrySelector";
import type { InputRef } from "ui/forms/Input";
import InputGroup from "ui/forms/InputGroupText";
import InputLabel from "ui/forms/InputLabel";
import type { OptionProps } from "ui/forms/Select";
import Select from "ui/forms/Select";
import StateSelector from "ui/forms/StateSelector";
import Textarea from "ui/forms/Textarea";
import IconInfoLine from "ui/icons/IconInfoLine";
import Tooltip from "ui/popovers/Tooltip";
import { Message } from "ui/text/Message";
import styles from "./CommunityDetailsForm.module.scss";

export type LocationUpdateFields = Partial<
  Pick<
    ClientLocationWithLevel,
    | "name"
    | "externalId"
    | "address"
    | "city"
    | "state"
    | "zip"
    | "country"
    | "phone"
    | "archivedAt"
    | "brochureSlipSheetUrl"
    | "salesMapUrl"
    | "salesMapUseShowroomIntro"
    | "displayOptionPrices"
    | "pricingVisibilitySettings"
    | "disableShowroomViewer"
    | "disableLots"
    | "showMapTab"
    | "allowHomePlans"
    | "logoUrl"
    | "enableLogoOnMaps"
    | "useOfficeAddress"
    | "officeAddress"
    | "officeCity"
    | "officeState"
    | "officeZip"
    | "officeCountry"
    | "generalNotes"
    | "govtCodeAndStandards"
    | "buildingCodeAnalysisDesignCriteria"
    | "brochureSettings"
    | "salesStaffId"
    | "templateCode"
    | "parentId"
  > & { locationLevel: string }
>;

interface CommunityDetailsFormProps {
  locationLevel: string;
  communityFields: LocationUpdateFields;
  setCommunityFields: (arg0: LocationUpdateFields) => void;
  salesStaff?: ClientUser[];
  salesStaffWarning?: ReactNode;
}

export default function CommunityDetailsForm({
  communityFields,
  setCommunityFields,
  salesStaff,
  locationLevel,
  salesStaffWarning,
}: CommunityDetailsFormProps) {
  const { user, isSuperAdmin, builder, hasPermission } = useUserContext();
  const [titleSheetInfoExpanded, setTitleSheetInfoExpanded] = useState(false);
  const zipInputRef = useRef<InputRef>(null);
  const officeZipInputRef = useRef<InputRef>(null);

  const staffOpts = salesStaff?.length
    ? salesStaff.map((sa) => ({
        label: getUserFullName(sa),
        value: sa.id,
      }))
    : [];
  const salesStaffOptions: OptionProps[] = [{ label: "None", value: null }, ...staffOpts];

  if (isSuperAdmin || user?.tempUserRole === "sales_agent") {
    assertIsNotNullOrUndefined(user);
    salesStaffOptions.push({
      label: getUserFullName(user),
      value: user.id,
    });
  }

  const builderPricingSettings = builder?.pricingVisibilitySettings;
  const pricingSettings = communityFields.pricingVisibilitySettings ?? builderPricingSettings;

  const handleOverridePricesToggle = () => {
    if (communityFields.pricingVisibilitySettings) {
      // Turning OFF override - revert to parent or builder settings
      setCommunityFields({
        ...communityFields,
        pricingVisibilitySettings: null,
      });
    } else {
      // Turning ON override - keep current settings but enable override
      setCommunityFields({
        ...communityFields,
        pricingVisibilitySettings: { ...pricingSettings },
      });
    }
  };

  const handlePricingCheckboxChange = (key: keyof BuilderPricingVisibilitySettings) => {
    setCommunityFields({
      ...communityFields,
      pricingVisibilitySettings: { ...pricingSettings, [key]: !pricingSettings?.[key] },
    });
  };

  return (
    <>
      <div className={styles.inputHalfWidthColumnWrapper}>
        <InputGroup
          type="text"
          autoComplete="organization"
          placeholder={`Enter ${locationLevel} name`}
          labelText={`${locationLevel} Name`}
          onChange={(name) => setCommunityFields({ ...communityFields, name })}
          inputValue={communityFields.name}
        />
        <InputGroup
          type="text"
          tooltip={EXTERNAL_ID_OVERVIEW_MESSAGE}
          placeholder="Enter External ID"
          labelText="External ID"
          onChange={(externalId) => setCommunityFields({ ...communityFields, externalId })}
          inputValue={communityFields.externalId}
        />
      </div>
      <div className={styles.inputHalfWidthColumnWrapper}>
        <InputGroup
          type="text"
          autoComplete="street-address"
          placeholder="Enter Address"
          labelText="Address"
          onChange={(address) => setCommunityFields({ ...communityFields, address })}
          inputValue={communityFields.address}
        />
        <InputGroup
          tooltip={TEMPLATE_CODE_DESCRIPTION("Location")}
          type="text"
          placeholder="Enter Template Code"
          labelText="Template Code"
          onChange={(templateCode) => setCommunityFields({ ...communityFields, templateCode })}
          inputValue={communityFields.templateCode}
        />
      </div>
      <div className={styles.inputGroupWrapper}>
        <div className={styles.inputColumnWrapper}>
          <InputGroup
            type="text"
            autoComplete="address-level2"
            placeholder="Enter City"
            labelText="City"
            onChange={(city) => setCommunityFields({ ...communityFields, city })}
            inputValue={communityFields.city}
          />
        </div>
        <div className={styles.inputColumnWrapper}>
          <InputLabel text="State" />
          <StateSelector
            countryCode={communityFields.country ?? undefined}
            initialValue={communityFields.state ?? undefined}
            onSelect={(state) => setCommunityFields({ ...communityFields, state })}
          />
        </div>
        <div className={styles.inputColumnWrapper}>
          <InputGroup
            ref={zipInputRef}
            type="text"
            autoComplete="postal-code"
            placeholder="Enter Zip/Postal Code"
            labelText="Zip/Postal Code"
            onChange={(zip) => setCommunityFields({ ...communityFields, zip })}
            validate={() => validateZipCode(communityFields.zip ?? undefined, communityFields.country ?? undefined)}
            inputValue={communityFields.zip}
          />
        </div>
      </div>
      <div className={styles.inputGroupWrapper}>
        <div className={styles.inputColumnWrapper}>
          <InputLabel text="Country" />
          <CountrySelector
            initialValue={communityFields.country ?? undefined}
            onSelect={(value) => {
              setCommunityFields({ ...communityFields, country: value, state: undefined });
              setTimeout(() => {
                zipInputRef.current?.validate();
              }, 0);
            }}
            allowedCountries={["US", "CA"]}
          />
        </div>
      </div>
      {salesStaff?.length || salesStaffWarning ? (
        <div className={styles.inputFullWidth}>
          <InputLabel text="Sales Agent" />
          <Select
            value={communityFields.salesStaffId}
            onChange={(salesStaffId) =>
              setCommunityFields({ ...communityFields, salesStaffId: ensureIsString(salesStaffId) })
            }
            disabled={!hasPermission("ASSIGN_SALES_AGENT_TO_COMMUNITY")}
            placeholder="None"
            options={salesStaffOptions}
          />
          {salesStaffWarning}
        </div>
      ) : null}
      <div className={styles.flexWrapper}>
        <Checkbox
          label="Disable 3D plan view for Showroom Public Links"
          checked={communityFields.disableShowroomViewer}
          onChange={() =>
            setCommunityFields({ ...communityFields, disableShowroomViewer: !communityFields.disableShowroomViewer })
          }
        />
        <Checkbox
          label="View Building Code Info"
          checked={titleSheetInfoExpanded}
          onChange={() => setTitleSheetInfoExpanded(!titleSheetInfoExpanded)}
        />
        <Checkbox
          label="Use Showroom Intro in public Sales Maps"
          checked={!!communityFields.salesMapUseShowroomIntro}
          onChange={() =>
            setCommunityFields({
              ...communityFields,
              salesMapUseShowroomIntro: !communityFields.salesMapUseShowroomIntro,
            })
          }
        />
        <Checkbox
          label="Display Office Address on PDF Plan Set"
          checked={communityFields.useOfficeAddress}
          onChange={() =>
            setCommunityFields({ ...communityFields, useOfficeAddress: !communityFields.useOfficeAddress })
          }
        />
      </div>
      <div className={styles.checkboxRow}>
        <div>
          <label htmlFor="showMapTab">Show Map Tab</label>
          <div className={styles.checkboxHelpText}>Disabling hides the Map tab on this location.</div>
        </div>
        <Toggle
          variants="small"
          ariaLabel="Show Map Tab toggle"
          id="showMapTab"
          isTrue={!!communityFields.showMapTab}
          onChange={() => setCommunityFields({ ...communityFields, showMapTab: !communityFields.showMapTab })}
        />
      </div>
      <div className={cx(styles.section, styles.hr)}></div>
      <div className={styles.pricingVisibilityHeader}>Pricing Visibility</div>
      <div className={styles.checkboxRow}>
        <label htmlFor="overridePrices">Override Price Visibility Settings</label>
        <Toggle
          variants="small"
          id="overridePrices"
          ariaLabel="Override prices toggle"
          isTrue={!!communityFields.pricingVisibilitySettings}
          onChange={handleOverridePricesToggle}
        />
      </div>
      <div className={styles.wrapper}>
        <div className="sections">
          <div className="section">
            <label className="label">
              {communityFields.pricingVisibilitySettings
                ? `Select which pricing information to display`
                : `Pricing settings inherited from "builder settings"`}
            </label>
            <div className="checkboxes">
              {objectTypedKeys(pricingVisibilitySettings).map((key) => (
                <div key={key} className={styles.checkboxWrapper}>
                  <Checkbox
                    label={pricingVisibilitySettings[key]?.label}
                    aria-label={`${key} pricing visibility checkbox`}
                    checked={!!pricingSettings?.[key]}
                    onChange={() => handlePricingCheckboxChange(key)}
                    disabled={communityFields.pricingVisibilitySettings === null}
                  />
                  <Tooltip.Root delayDuration={0}>
                    <Tooltip.Trigger>
                      <span className={styles.infoIcon}>
                        <IconInfoLine />
                      </span>
                    </Tooltip.Trigger>
                    <Tooltip.Content>{pricingVisibilitySettings[key]?.description ?? ""}</Tooltip.Content>
                  </Tooltip.Root>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      {titleSheetInfoExpanded && (
        <>
          <div style={{ marginTop: 12, marginBottom: 12 }}>
            <Message>
              The below information will be rendered within the title sheet of this location&apos;s generated plan set.
            </Message>
          </div>
          <div>
            <label htmlFor="generalNotes" className={styles.textAreaLabel}>
              General Notes
            </label>
            <Textarea
              id="generalNotes"
              value={communityFields.generalNotes ?? undefined}
              onChange={(e) => setCommunityFields({ ...communityFields, generalNotes: e.target.value })}
            />
          </div>
          <div className="mt-6">
            <label htmlFor="govtCodeAndStandards" className={styles.textAreaLabel}>
              Gov&apos;t Code & Standards
            </label>
            <Textarea
              id="govtCodeAndStandards"
              value={communityFields.govtCodeAndStandards ?? undefined}
              onChange={(e) => setCommunityFields({ ...communityFields, govtCodeAndStandards: e.target.value })}
            />
          </div>
          <div className="mt-6">
            <label htmlFor="buildingCodeAnalysisDesignCriteria" className={styles.textAreaLabel}>
              Building Code Analysis / Design Criteria
            </label>
            <Textarea
              id="buildingCodeAnalysisDesignCriteria"
              value={communityFields.buildingCodeAnalysisDesignCriteria ?? undefined}
              onChange={(e) =>
                setCommunityFields({ ...communityFields, buildingCodeAnalysisDesignCriteria: e.target.value })
              }
            />
          </div>
        </>
      )}
      {communityFields.useOfficeAddress && (
        <>
          <div style={{ marginTop: 12, marginBottom: 12 }}>
            <Message>
              The address below will be used in place of your builder&apos;s primary address within the title sheets of
              this location&apos;s generated plan sets.
            </Message>
          </div>
          <div>
            <InputGroup
              type="text"
              autoComplete="street-address"
              placeholder="Enter Address"
              labelText="Office Address"
              onChange={(officeAddress) => setCommunityFields({ ...communityFields, officeAddress })}
              inputValue={communityFields.officeAddress}
            />
          </div>
          <div className={styles.inputGroupWrapper}>
            <div className={styles.inputColumnWrapper}>
              <InputGroup
                type="text"
                autoComplete="address-level2"
                placeholder="Enter City"
                labelText="Office City"
                onChange={(officeCity) => setCommunityFields({ ...communityFields, officeCity })}
                inputValue={communityFields.officeCity}
              />
            </div>
            <div className={styles.inputColumnWrapper}>
              <InputLabel text="Office State" />
              <StateSelector
                countryCode={communityFields.officeCountry ?? undefined}
                initialValue={communityFields.officeState ?? undefined}
                onSelect={(officeState) => setCommunityFields({ ...communityFields, officeState })}
              />
            </div>
            <div className={styles.inputColumnWrapper}>
              <InputGroup
                ref={officeZipInputRef}
                type="text"
                autoComplete="postal-code"
                placeholder="Enter Zip/Postal Code"
                labelText="Office Zip/Postal Code"
                onChange={(officeZip) => setCommunityFields({ ...communityFields, officeZip })}
                validate={() =>
                  validateZipCode(communityFields.officeZip ?? undefined, communityFields.officeCountry ?? undefined)
                }
                inputValue={communityFields.officeZip}
              />
            </div>
          </div>
          <div className={styles.inputGroupWrapper}>
            <div className={styles.inputColumnWrapper}>
              <InputLabel text="Office Country" />
              <CountrySelector
                initialValue={communityFields.officeCountry ?? undefined}
                onSelect={(value) => {
                  setCommunityFields({ ...communityFields, officeCountry: value, officeState: undefined });
                  setTimeout(() => {
                    officeZipInputRef.current?.validate();
                  }, 0);
                }}
                allowedCountries={["US", "CA"]}
              />
            </div>
          </div>
        </>
      )}
    </>
  );
}
